"use client";

import {
  BookCheck,
  CircleDot,
  Coffee,
  GraduationCap,
  Loader2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useI18n } from "@/locales/client";
import type { NoLongerNeedReason } from "../../hooks/use-cancellation-modal";

const SUB_REASONS = [
  {
    value: "graduated" as const,
    labelKey: "cancellation.noLongerNeed.graduated" as const,
    icon: GraduationCap,
  },
  {
    value: "exam_over" as const,
    labelKey: "cancellation.noLongerNeed.examOver" as const,
    icon: BookCheck,
  },
  {
    value: "taking_break" as const,
    labelKey: "cancellation.noLongerNeed.takingBreak" as const,
    icon: Coffee,
  },
  {
    value: "other_reason" as const,
    labelKey: "cancellation.noLongerNeed.otherReason" as const,
    icon: CircleDot,
  },
];

interface NoLongerNeedReasonStepProps {
  selectedSubReason: NoLongerNeedReason | null;
  onSelectSubReason: (reason: NoLongerNeedReason) => void;
  onBack: () => void;
  onContinue: () => void;
  isLoading?: boolean;
}

export const NoLongerNeedReasonStep = ({
  selectedSubReason,
  onSelectSubReason,
  onBack,
  onContinue,
  isLoading = false,
}: NoLongerNeedReasonStepProps) => {
  const t = useI18n();

  return (
    <div className="flex flex-col gap-6">
      <p className="text-sm text-muted-foreground">
        {t("cancellation.noLongerNeed.description")}
      </p>

      <div className="flex flex-col gap-2">
        {SUB_REASONS.map((reason) => (
          <button
            key={reason.value}
            type="button"
            onClick={() => onSelectSubReason(reason.value)}
            className={cn(
              "flex items-center gap-3 rounded-lg border p-3.5 text-left transition-colors cursor-pointer",
              selectedSubReason === reason.value
                ? "border-brand-primary bg-brand-primary/5"
                : "border-border hover:border-muted-foreground/30",
            )}
          >
            <reason.icon className="size-4 shrink-0 text-muted-foreground" />
            <span className="text-sm text-foreground">
              {t(reason.labelKey)}
            </span>
          </button>
        ))}
      </div>

      <div className="flex items-center justify-between">
        <Button variant="ghost" onClick={onBack} size="sm">
          {t("cancellation.reason.goBack")}
        </Button>
        <Button
          onClick={onContinue}
          disabled={!selectedSubReason || isLoading}
          size="sm"
          className="border border-red-200 bg-white text-destructive shadow-sm hover:bg-slate-100"
        >
          {isLoading && <Loader2 className="mr-2 size-4 animate-spin" />}
          {t("cancellation.reason.continueCanceling")}
        </Button>
      </div>
    </div>
  );
};
